import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment, payoffCard } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function StudentLoanPage() {
    const [balance, setBalance] = useState(40000);
    const [rate, setRate] = useState(6.8);
    const [years, setYears] = useState(10);
    const [extra, setExtra] = useState(0);

    const result = useMemo(() => {
        const bal = toNum(balance);
        const r = toNum(rate);
        const yrs = Math.max(1, toNum(years));
        const baseMonthly = monthlyPayment(bal, r, yrs);
        // Use payoffCard math for extra-payment scenario (single balance, fixed payment)
        const withExtra = payoffCard(bal, r, baseMonthly, toNum(extra));
        const baseTotalInterest = baseMonthly * yrs * 12 - bal;
        const monthsSaved = yrs * 12 - withExtra.months;
        const interestSaved = baseTotalInterest - withExtra.totalInterest;
        return {
            monthly: baseMonthly,
            withExtraMonths: withExtra.months,
            withExtraInterest: withExtra.totalInterest,
            baseTotalInterest,
            monthsSaved: Math.max(0, monthsSaved),
            interestSaved: Math.max(0, interestSaved),
            yearsSaved: Math.max(0, monthsSaved) / 12,
        };
    }, [balance, rate, years, extra]);

    return (
        <CalculatorShell
            slug="student-loan"
            explainer={
                                <>
                    <h2>How this student loan calculator works</h2>
                    <p>
                        A student loan calculator estimates your monthly payment and total interest for federal
                        or private student loans, and compares standard, graduated, and income-driven
                        repayment plans side by side. Use it to choose the right repayment strategy or to
                        model the impact of extra payments.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Standard repayment:</strong> fixed payment over 10 years — the lowest total interest of any plan.</li>
                        <li><strong>Graduated repayment:</strong> payments start lower and increase every two years, assuming income grows over time.</li>
                        <li><strong>Income-driven repayment (IDR):</strong> capped at 5–20% of discretionary income with forgiveness after 20–25 years depending on the plan.</li>
                        <li><strong>Unsubsidized loan interest:</strong> accrues during school, grace periods, and deferment — adding to your balance before repayment even begins.</li>
                        <li><strong>Capitalization:</strong> unpaid interest added to principal increases the balance on which future interest is calculated, compounding the cost.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Pay the highest-rate loans first while making minimums on the rest — the avalanche method saves the most interest.</li>
                        <li>If possible, make interest payments during school on unsubsidized loans to prevent capitalization at graduation.</li>
                        <li>Public Service Loan Forgiveness (PSLF) cancels remaining federal loan balances after 120 qualifying payments for government and nonprofit employees.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Loan balance" prefix="$" value={balance} onChange={setBalance} dataTestId="calc-input-balance" />
                    <Field label="Interest rate (APR)" suffix="%" value={rate} onChange={setRate} dataTestId="calc-input-rate" />
                    <Field label="Repayment term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                    <Field label="Extra monthly payment" prefix="$" value={extra} onChange={setExtra} dataTestId="calc-input-extra" hint="Set 0 for the standard plan" />
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Monthly payment"
                        value={result.monthly}
                        format={(n) => fmtUSD(n, { decimals: 2 })}
                        secondary={[
                            { label: "Total interest (standard)", value: result.baseTotalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-total-interest" },
                            ...(toNum(extra) > 0 ? [
                                { label: "Years saved with extra", value: result.yearsSaved, format: (n) => `${n.toFixed(1)} yrs`, testId: "calc-result-years-saved" },
                                { label: "Interest saved", value: result.interestSaved, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-interest-saved" },
                            ] : []),
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
